import {useState, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import Modal from "../feedBack/Modal";
import {useStateContext} from "../../contexts/ContextProvider";
import {useFacilityWithChildren, Facility} from "../../pages/settings/facilities/useFacility";
import {BuildingOffice2Icon, BuildingOfficeIcon, CheckCircleIcon, MagnifyingGlassIcon} from "@heroicons/react/24/solid";
import Loader from "../dataDisplay/Loader";

interface FacilitySwitcherModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
}

export default function FacilitySwitcherModal({open, setOpen}: FacilitySwitcherModalProps) {
    const navigate = useNavigate();
    const {currentFacility, setCurrentFacility, user} = useStateContext();
    const [search, setSearch] = useState("");

    // Seuls les admins de groupe peuvent utiliser ce modal
    // On récupère le groupe de l'utilisateur (son facility d'origine qui est un groupe)
    const groupFacilityId = user?.facility?.is_group ? user.facility.id : null;

    // Récupérer le groupe avec ses établissements enfants
    const {data: groupFacility, isLoading} = useFacilityWithChildren(groupFacilityId);

    const handleSelectFacility = (facility: Facility) => {
        const isChangingFacility = currentFacility?.id !== facility.id;
        setCurrentFacility(facility);
        setOpen(false);
        setSearch("");

        // Rediriger vers /settings/facilities après un changement d'établissement
        if (isChangingFacility) {
            navigate('/settings/facilities');
        }
    };

    // Liste de toutes les facilities disponibles (groupe + établissements enfants)
    const allFacilities = groupFacility
        ? [groupFacility, ...(groupFacility.child_facilities || [])]
        : [];

    // Filtrer les facilities selon la recherche
    const filteredFacilities = useMemo(() => {
        if (!search.trim()) return allFacilities;
        const searchLower = search.toLowerCase().trim();
        return allFacilities.filter(facility =>
            facility.name?.toLowerCase().includes(searchLower) ||
            facility.city?.toLowerCase().includes(searchLower)
        );
    }, [allFacilities, search]);

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title="Changer d'établissement"
            size="md"
            showFooter={false}
        >
            {isLoading ? (
                <div className="flex justify-center py-6">
                    <Loader message="Chargement..."/>
                </div>
            ) : (
                <div>
                    {/* Barre de recherche */}
                    {allFacilities.length > 5 && (
                        <div className="relative mb-3">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"/>
                            <input
                                type="text"
                                placeholder="Rechercher un établissement..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-color focus:border-transparent"
                            />
                        </div>
                    )}

                    <div className="max-h-[350px] overflow-y-auto -mx-4 px-4">
                        {filteredFacilities.length === 0 ? (
                            <p className="text-center text-gray-500 py-6 text-sm">
                                {search ? "Aucun établissement trouvé" : "Aucun établissement disponible"}
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {filteredFacilities.map((facility) => {
                                const isActive = currentFacility?.id === facility.id;
                                const isGroup = facility.is_group;

                                return (
                                    <button
                                        key={facility.id}
                                        onClick={() => handleSelectFacility(facility)}
                                        className={`w-full text-left bg-white px-3 py-2.5 rounded-lg transition-all duration-150 flex items-center gap-3 border ${
                                            isActive
                                                ? 'border-main-color'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {/* Icône */}
                                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                                            isGroup
                                                ? (isActive ? 'bg-primary-100' : 'bg-purple-100')
                                                : (isActive ? 'bg-primary-100' : 'bg-gray-100')
                                        }`}>
                                            {isGroup ? (
                                                <BuildingOffice2Icon className={`h-4 w-4 ${isActive ? 'text-main-color' : 'text-purple-600'}`}/>
                                            ) : (
                                                <BuildingOfficeIcon className={`h-4 w-4 ${isActive ? 'text-main-color' : 'text-gray-600'}`}/>
                                            )}
                                        </div>

                                        {/* Nom */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm truncate ${isActive ? 'font-semibold text-main-color' : 'font-medium text-gray-900'}`}>
                                                    {facility.name}
                                                </span>
                                                {isGroup ? (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                                                        Groupe
                                                    </span>
                                                ):null}
                                            </div>
                                            {facility.city ? (
                                                <p className="text-xs text-gray-500 truncate">
                                                    {facility.city}
                                                </p>
                                            ):null}
                                        </div>

                                        {/* Indicateur actif */}
                                        {isActive && (
                                            <CheckCircleIcon className="h-5 w-5 text-main-color flex-shrink-0"/>
                                        )}
                                    </button>
                                );
                            })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}
